import React, { useState, useMemo } from 'react';
import { 
  ChartBarIcon, 
  DocumentTextIcon, 
  ArrowPathIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart
} from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import AdvancedCharts from './AdvancedCharts';

interface AnalysisResultsProps {
  data: {
    success: boolean;
    filename: string;
    sheets: string[];
    analysis: {
      monthlyData: any[];
      weeklyData: any[];
      weeklyObjectives?: any[];
      objectives: any[];
      comparisons: any[];
      kpis: {
        // Année N-1 - Annuel
        totalRevenueAnnualPrevious: string;
        totalFreqAnnualPrevious: string;
        avgPMAnnualPrevious: string;
        
        // Année N-1 - À date
        totalRevenueToDatePrevious: string;
        totalFreqToDatePrevious: string;
        avgPMToDatePrevious: string;
        avgMonthlyToDatePrevious: string;
        
        // Année N - Annuel
        totalRevenueAnnualCurrent: string;
        totalFreqAnnualCurrent: string;
        avgPMAnnualCurrent: string;
        
        // Année N - À date
        totalRevenueToDateCurrent: string;
        totalFreqToDateCurrent: string;
        avgPMToDateCurrent: string;
        avgMonthlyToDateCurrent: string;
        
        // Objectifs
        totalObjectivesToDate: string;
        totalObjectivesAnnual: string;
        objectiveRealizationToDate: string;
        objectiveRealizationAnnual: string;
        
        // Objectifs par métrique
        totalCAObjectivesAnnual: string;
        totalFreqObjectivesAnnual: string;
        totalPMObjectivesAnnual: string;
        
        // Comparaisons à date
        revenueGrowthToDate: string;
        freqGrowthToDate: string;
        pmGrowthToDate: string;
        
        // Comparaisons annuelles
        revenueGrowthAnnual: string;
        freqGrowthAnnual: string;
        pmGrowthAnnual: string;
        
        // Métadonnées
        yearPrevious: number;
        yearCurrent: number;
        currentMonth: number;
        currentMonthName: string;
      };
      summary: string;
      currentMonth: number;
      availableMetrics: string[];
    };
    rawData: {
      headers: string[];
      rowCount: number;
    };
  };
  onNewAnalysis: () => void;
  onGenerateReport: () => void;
  isDataRestored?: boolean;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  data,
  onNewAnalysis,
  onGenerateReport,
  isDataRestored = false
}) => {
  const [activeChart, setActiveChart] = useState<'monthly' | 'weekly'>('monthly');
  const [selectedMetric, setSelectedMetric] = useState<string>('CA');
  const [selectedYear, setSelectedYear] = useState<string>('both');
  const [weeklyPage, setWeeklyPage] = useState<number>(1);
  const [weeklyPageSize] = useState<number>(10);

  const { analysis } = data;

  // Préparer les données pour les graphiques mensuels
  const prepareMonthlyChartData = useMemo(() => {
    const filteredData = analysis.monthlyData.filter(item => {
      // Mapper les valeurs du dropdown vers les métriques des données
      let targetMetric = selectedMetric;
      if (selectedMetric === 'Fréquentation') targetMetric = 'FREQ';
      if (selectedMetric === 'Chiffre d\'affaires') targetMetric = 'CA';
      
      const metricMatch = selectedMetric === 'all' || item.metric === targetMetric;
      const yearMatch = selectedYear === 'both' || 
                       (selectedYear === analysis.kpis.yearPrevious.toString() && item.year === analysis.kpis.yearPrevious) ||
                       (selectedYear === analysis.kpis.yearCurrent.toString() && item.year === analysis.kpis.yearCurrent);
      // Afficher TOUTES les données (12 mois) pas seulement à date
      return metricMatch && yearMatch;
    });

    // Filtrer les objectifs selon la métrique sélectionnée
    const filteredObjectives = analysis.objectives.filter(obj => {
      let targetMetric = selectedMetric;
      if (selectedMetric === 'Fréquentation') targetMetric = 'FREQ';
      if (selectedMetric === 'Chiffre d\'affaires') targetMetric = 'CA';
      
      return obj.metric === targetMetric && obj.year === analysis.kpis.yearCurrent;
    });

    // Grouper par mois pour comparaison
    const monthlyMap = new Map();
    
    filteredData.forEach(item => {
      const monthKey = item.month;
      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, {
          month: item.month,
          monthNumber: item.monthNumber,
          [analysis.kpis.yearPrevious]: 0,
          [analysis.kpis.yearCurrent]: 0,
          objectif: 0,
          variation: 0
        });
      }
      
      const monthData = monthlyMap.get(monthKey);
      monthData[item.year.toString()] = item.value;
    });

    // Ajouter les objectifs
    filteredObjectives.forEach(obj => {
      const monthKey = obj.type; // obj.type contient le nom du mois
      if (monthlyMap.has(monthKey)) {
        const monthData = monthlyMap.get(monthKey);
        monthData.objectif = obj.objective;
      }
    });

    // Calculer les variations et trier
    const chartData = Array.from(monthlyMap.values())
      .map(item => {
        const prevYearValue = item[analysis.kpis.yearPrevious];
        const currentYearValue = item[analysis.kpis.yearCurrent];
        const variation = prevYearValue > 0 ? 
          ((currentYearValue - prevYearValue) / prevYearValue * 100) : 0;
        return {
          ...item,
          variation: parseFloat(variation.toFixed(2))
        };
      })
      .sort((a, b) => a.monthNumber - b.monthNumber);

    return chartData;
  }, [analysis.monthlyData, analysis.objectives, selectedMetric, selectedYear, analysis.kpis.yearPrevious, analysis.kpis.yearCurrent]);

  // Préparer les données hebdomadaires pour le graphique à barres comparatif
  const prepareWeeklyChartData = useMemo(() => {
    if (!analysis.weeklyData || analysis.weeklyData.length === 0) {
      return [];
    }

    // Filtrer selon la métrique sélectionnée
    let targetMetric = selectedMetric;
    if (selectedMetric === 'Fréquentation') targetMetric = 'FREQ';
    if (selectedMetric === 'Chiffre d\'affaires') targetMetric = 'CA';

    const filteredData = analysis.weeklyData.filter(item => 
      item.metric === targetMetric
    );

    // Récupérer les vrais objectifs hebdomadaires depuis Excel
    const weeklyObjectivesMap = new Map();
    if (analysis.weeklyObjectives && analysis.weeklyObjectives.length > 0) {
      analysis.weeklyObjectives.forEach((obj: any) => {
        if (obj.metric === targetMetric) {
          weeklyObjectivesMap.set(obj.weekNumber, obj.objective);
        }
      });
      console.log(`🎯 Objectifs hebdomadaires trouvés pour ${targetMetric}:`, weeklyObjectivesMap.size);
      
      // Debug : afficher quelques objectifs
      if (weeklyObjectivesMap.size > 0) {
        console.log(`Exemple objectifs ${targetMetric}:`, 
          Array.from(weeklyObjectivesMap.entries()).slice(0, 3).map(([week, obj]) => `S${week}: ${obj}`));
      }
    }

    // Grouper par semaine pour comparaison
    const weeklyMap = new Map();
    
    filteredData.forEach(item => {
      const weekKey = `S${item.weekNumber}`;
      if (!weeklyMap.has(weekKey)) {
        weeklyMap.set(weekKey, {
          week: weekKey,
          weekNumber: item.weekNumber,
          [analysis.kpis.yearPrevious]: 0,
          [analysis.kpis.yearCurrent]: 0,
          objectif: weeklyObjectivesMap.get(item.weekNumber) || 0, // Objectif réel depuis Excel
          variation: 0
        });
      }
      
      const weekData = weeklyMap.get(weekKey);
      weekData[item.year.toString()] = item.value;
    });

    // Calculer les variations et trier
    const chartData = Array.from(weeklyMap.values())
      .map(item => {
        const prevYearValue = item[analysis.kpis.yearPrevious];
        const currentYearValue = item[analysis.kpis.yearCurrent];
        const variation = prevYearValue > 0 ? 
          ((currentYearValue - prevYearValue) / prevYearValue * 100) : 0;
        return {
          ...item,
          variation: parseFloat(variation.toFixed(2))
        };
      })
      .sort((a, b) => a.weekNumber - b.weekNumber);

    return chartData;
  }, [analysis.weeklyData, analysis.weeklyObjectives, selectedMetric, analysis.kpis.yearPrevious, analysis.kpis.yearCurrent]);

  // Préparer les données de comparaison hebdomadaire pour le tableau avec pagination
  const prepareWeeklyComparisonData = useMemo(() => {
    if (!analysis.weeklyData || analysis.weeklyData.length === 0) {
      return { data: [], totalPages: 0 };
    }

    // Utiliser la même métrique que le graphique
    let metricKey = selectedMetric;
    if (selectedMetric === 'Fréquentation') metricKey = 'FREQ';
    
    // Récupérer les vrais objectifs hebdomadaires depuis Excel
    const weeklyObjectivesMap = new Map();
    if (analysis.weeklyObjectives && analysis.weeklyObjectives.length > 0) {
      analysis.weeklyObjectives.forEach((obj: any) => {
        if (obj.metric === metricKey) {
          weeklyObjectivesMap.set(obj.weekNumber, obj.objective);
        }
      });
    }
    
    // Créer une map des données hebdomadaires par semaine et année
    const weeklyDataMap = new Map();
    analysis.weeklyData.filter(item => item.metric === metricKey)
      .forEach(item => {
        const key = `S${item.weekNumber}`;
        if (!weeklyDataMap.has(key)) {
          weeklyDataMap.set(key, {
            category: `Semaine ${item.weekNumber}`,
            weekNumber: item.weekNumber,
            'Année Current': 0,
            'Année Previous': 0,
            'Objectif': weeklyObjectivesMap.get(item.weekNumber) || 0, // Objectif réel depuis Excel
            variation: 0,
            'Variation vs Objectif': 0
          });
        }
        
        const weekData = weeklyDataMap.get(key);
        if (item.year === analysis.kpis.yearCurrent) {
          weekData['Année Current'] = item.value;
        } else if (item.year === analysis.kpis.yearPrevious) {
          weekData['Année Previous'] = item.value;
        }
      });

    // Calculer les variations
    const comparisonData = Array.from(weeklyDataMap.values())
      .map(item => {
        const currentValue = item['Année Current'];
        const previousValue = item['Année Previous'];
        const objectiveValue = item['Objectif'];
        
        // Variation N vs N-1
        const variation = previousValue > 0 ? 
          ((currentValue - previousValue) / previousValue * 100) : 0;
        
        // Variation vs objectif
        const objectiveVariation = objectiveValue > 0 ? 
          ((currentValue - objectiveValue) / objectiveValue * 100) : 0;
        
        return {
          ...item,
          variation: parseFloat(variation.toFixed(2)),
          'Variation vs Objectif': parseFloat(objectiveVariation.toFixed(2))
        };
      })
      .sort((a, b) => a.weekNumber - b.weekNumber);

    // Pagination
    const totalPages = Math.ceil(comparisonData.length / weeklyPageSize);
    const startIndex = (weeklyPage - 1) * weeklyPageSize;
    const paginatedData = comparisonData.slice(startIndex, startIndex + weeklyPageSize);

    return { data: paginatedData, totalPages, totalItems: comparisonData.length };
  }, [analysis.weeklyData, analysis.weeklyObjectives, selectedMetric, analysis.kpis.yearCurrent, analysis.kpis.yearPrevious, weeklyPage, weeklyPageSize]);

  // Préparer les données hebdomadaires (ancienne version - gardée pour compatibilité)
  const prepareWeeklyData = useMemo(() => {
    const filteredData = analysis.weeklyData.filter(item => {
      // Mapper les valeurs du dropdown vers les métriques des données
      let targetMetric = selectedMetric;
      if (selectedMetric === 'Fréquentation') targetMetric = 'FREQ';
      if (selectedMetric === 'Chiffre d\'affaires') targetMetric = 'CA';
      
      const metricMatch = selectedMetric === 'all' || item.metric === targetMetric;
      const yearMatch = selectedYear === 'both' || 
                       (selectedYear === analysis.kpis.yearPrevious.toString() && item.year === analysis.kpis.yearPrevious) ||
                       (selectedYear === analysis.kpis.yearCurrent.toString() && item.year === analysis.kpis.yearCurrent);
      return metricMatch && yearMatch;
    });

    return filteredData
      .sort((a, b) => a.weekNumber - b.weekNumber)
      .slice(0, 20)
      .map(item => ({
        semaine: `S${item.weekNumber}`,
        valeur: item.value,
        annee: item.year.toString(),
        categorie: item.category
      }));
  }, [analysis.weeklyData, selectedMetric, selectedYear, analysis.kpis.yearPrevious, analysis.kpis.yearCurrent]);

  // Préparer les données de comparaison pour le tableau mensuel (utilise le même filtre que le graphique)
  const prepareComparisonData = useMemo(() => {
    // Utiliser la même métrique que le graphique
    let metricKey = selectedMetric;
    if (selectedMetric === 'Fréquentation') metricKey = 'FREQ';
    
    // Créer une map des objectifs par mois pour la métrique sélectionnée
    const objectivesMap = new Map();
    analysis.objectives.filter(obj => obj.metric === metricKey && obj.year === analysis.kpis.yearCurrent)
      .forEach(obj => {
        objectivesMap.set(obj.type, obj.objective); // obj.type contient le nom du mois
      });
    
    // Créer une map des données mensuelles par mois et année
    const monthlyDataMap = new Map();
    analysis.monthlyData.filter(item => item.metric === metricKey)
      .forEach(item => {
        const key = `${item.month}_${item.year}`;
        monthlyDataMap.set(key, item.value);
      });

    // Générer les données pour tous les 12 mois
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    
    return months.map(monthName => {
      const currentYearValue = monthlyDataMap.get(`${monthName}_${analysis.kpis.yearCurrent}`) || 0;
      const previousYearValue = monthlyDataMap.get(`${monthName}_${analysis.kpis.yearPrevious}`) || 0;
      const objectiveValue = objectivesMap.get(monthName) || 0;

      // Calculer la variation N vs N-1
      const variation = previousYearValue > 0 ? (((currentYearValue - previousYearValue) / previousYearValue) * 100) : 0;
      
      // Calculer la variation vs objectif (écart en pourcentage)
      const objectiveVariation = objectiveValue > 0 ? (((currentYearValue - objectiveValue) / objectiveValue) * 100) : 0;
       
       return {
       category: monthName,
       'Année Current': currentYearValue,
       'Année Previous': previousYearValue,
       'Objectif': objectiveValue,
       'Variation vs Objectif': objectiveVariation,
       variation: variation
     };
    });
  }, [analysis.monthlyData, analysis.objectives, analysis.kpis.yearCurrent, analysis.kpis.yearPrevious, selectedMetric]);

  const exportToPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      
      // Fonction pour formater les nombres avec des espaces comme séparateurs
      const formatNumber = (num: string | number): string => {
        const numValue = typeof num === 'string' ? parseFloat(num) : num;
        if (isNaN(numValue)) return '0';
        return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      };
      
      // Créer le PDF en format A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      
      // Fonction pour ajouter l'en-tête avec logo sur chaque page
      const addHeader = (pageNumber: number, totalPages: number) => {
        // Fond en-tête noir
        pdf.setFillColor(26, 26, 26);
        pdf.rect(0, 0, pageWidth, 25, 'F');
        
        // Ligne accent orange
        pdf.setFillColor(217, 119, 6);
        pdf.rect(0, 25, pageWidth, 2, 'F');
        
        // Ajouter le logo (si disponible)
        try {
          // Vous devez placer votre logo dans le dossier public/logo-paries.png
          const logoImg = new Image();
          logoImg.src = '/logo-paries.png'; // Chemin vers votre logo
          
          // Ajouter le logo avec proportions correctes (plus haut, moins large)
          pdf.addImage(logoImg, 'PNG', margin, 3, 30, 18); // x, y, width, height - dimensions ajustées
          
        } catch (error) {
          // Fallback si le logo n'est pas disponible
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text('PARIÈS', margin, 17);
          
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.text('GOURMANDISES BASQUES', margin + 25, 17);
        }
        
        // Numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(200, 200, 200);
        pdf.text(`Page ${pageNumber}/${totalPages}`, pageWidth - margin - 20, 17);
        
        // Reset couleur texte
        pdf.setTextColor(26, 26, 26);
      };
      
      // Fonction pour ajouter le pied de page
      const addFooter = () => {
        const footerY = pageHeight - 15;
        pdf.setFillColor(249, 250, 251);
        pdf.rect(0, footerY - 5, pageWidth, 20, 'F');
        
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.setFont('helvetica', 'italic');
        const footerText = `Rapport généré automatiquement le ${new Date().toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}`;
        pdf.text(footerText, margin, footerY);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Analyse SOCOA - Pariès', pageWidth - margin - 40, footerY);
      };
      
      let currentPage = 1;
      const totalPages = 3;
      
      // PAGE 1 - Titre et KPIs principaux
      addHeader(currentPage, totalPages);
      let yPos = 40;
      
      // Titre principal
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(26, 26, 26);
      pdf.text('ANALYSE FINANCIÈRE SOCOA', margin, yPos);
      yPos += 15;
      
      // Sous-titre avec informations
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      const subtitle = `Période : Données jusqu'à ${analysis.kpis.currentMonthName} ${analysis.kpis.yearCurrent} • Fichier : ${data.filename}`;
      pdf.text(subtitle, margin, yPos);
      yPos += 20;
      
      // Carte d'avancement principale
      const cardWidth = 60;
      const cardHeight = 25;
      
      pdf.setFillColor(217, 119, 6);
      pdf.rect(margin, yPos, cardWidth, cardHeight, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${analysis.kpis.objectiveRealizationToDate}%`, margin + 5, yPos + 12);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('🎯 Avancement CA vs Objectif ({analysis.kpis.currentMonthName})', margin + 5, yPos + 18);
      yPos += 35;
      
      // Section Année N-1
      pdf.setFillColor(59, 130, 246);
      pdf.rect(margin, yPos, maxWidth, 8, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`ANNÉE ${analysis.kpis.yearPrevious} (N-1)`, margin + 5, yPos + 6);
      yPos += 15;
      
      // KPIs N-1 en colonnes
      const kpisN1 = [
        { label: 'CA Total Annuel', value: `${formatNumber(analysis.kpis.totalRevenueAnnualPrevious)} €` },
        { label: 'CA à date', value: `${formatNumber(analysis.kpis.totalRevenueToDatePrevious)} €` },
        { label: 'Fréquentation', value: `${formatNumber(analysis.kpis.totalFreqToDatePrevious)} visiteurs` },
        { label: 'Panier Moyen', value: `${formatNumber(analysis.kpis.avgPMToDatePrevious)} €` }
      ];
      
      pdf.setTextColor(26, 26, 26);
      pdf.setFontSize(10);
      
      kpisN1.forEach((kpi, index) => {
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = margin + (col * (maxWidth / 2));
        const y = yPos + (row * 12);
        
        pdf.setFillColor(239, 246, 255);
        pdf.rect(x, y - 2, (maxWidth / 2) - 5, 10, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.text(kpi.label + ':', x + 2, y + 3);
        pdf.setFont('helvetica', 'normal');
        pdf.text(kpi.value, x + 2, y + 7);
      });
      yPos += 30;
      
      // Section Année N
      pdf.setFillColor(16, 185, 129);
      pdf.rect(margin, yPos, maxWidth, 8, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`ANNÉE ${analysis.kpis.yearCurrent} (N)`, margin + 5, yPos + 6);
      yPos += 15;
      
      // KPIs N en colonnes
      const kpisN = [
        { label: 'CA Total Annuel', value: `${formatNumber(analysis.kpis.totalRevenueAnnualCurrent)} €` },
        { label: 'CA à date', value: `${formatNumber(analysis.kpis.totalRevenueToDateCurrent)} €` },
        { label: 'Fréquentation', value: `${formatNumber(analysis.kpis.totalFreqToDateCurrent)} visiteurs` },
        { label: 'Panier Moyen', value: `${formatNumber(analysis.kpis.avgPMToDateCurrent)} €` }
      ];
      
      pdf.setTextColor(26, 26, 26);
      pdf.setFontSize(10);
      
      kpisN.forEach((kpi, index) => {
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = margin + (col * (maxWidth / 2));
        const y = yPos + (row * 12);
        
        pdf.setFillColor(236, 253, 245);
        pdf.rect(x, y - 2, (maxWidth / 2) - 5, 10, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.text(kpi.label + ':', x + 2, y + 3);
        pdf.setFont('helvetica', 'normal');
        pdf.text(kpi.value, x + 2, y + 7);
      });
      yPos += 30;
      
      // Objectifs
      pdf.setFillColor(217, 119, 6);
      pdf.rect(margin, yPos, maxWidth, 8, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`OBJECTIFS ${analysis.kpis.yearCurrent}`, margin + 5, yPos + 6);
      yPos += 15;
      
      const objectifs = [
        { label: 'Objectif CA', value: `${formatNumber(analysis.kpis.totalCAObjectivesAnnual || '0')} €` },
        { label: 'Objectif Fréquentation', value: `${formatNumber(analysis.kpis.totalFreqObjectivesAnnual || '0')} visiteurs` },
        { label: 'Objectif PM', value: `${formatNumber(analysis.kpis.totalPMObjectivesAnnual || '0')} €` }
      ];
      
      pdf.setTextColor(26, 26, 26);
      pdf.setFontSize(10);
      
      objectifs.forEach((obj, index) => {
        const col = index % 3;
        const x = margin + (col * (maxWidth / 3));
        const y = yPos;
        
        pdf.setFillColor(255, 251, 235);
        pdf.rect(x, y - 2, (maxWidth / 3) - 5, 10, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.text(obj.label + ':', x + 2, y + 3);
        pdf.setFont('helvetica', 'normal');
        pdf.text(obj.value, x + 2, y + 7);
      });
      
      addFooter();
      
      // PAGE 2 - Évolutions et synthèse
      pdf.addPage();
      currentPage++;
      addHeader(currentPage, totalPages);
      yPos = 40;
      
      // Évolutions
      pdf.setFillColor(147, 51, 234);
      pdf.rect(margin, yPos, maxWidth, 8, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`ÉVOLUTIONS ${analysis.kpis.yearCurrent} vs ${analysis.kpis.yearPrevious} (à date - ${analysis.kpis.currentMonthName})`, margin + 5, yPos + 6);
      yPos += 15;
      
      const evolutions = [
        { 
          label: 'Évolution CA', 
          value: `${parseFloat(analysis.kpis.revenueGrowthToDate) >= 0 ? '+' : ''}${analysis.kpis.revenueGrowthToDate}%`,
          positive: parseFloat(analysis.kpis.revenueGrowthToDate) >= 0
        },
        { 
          label: 'Évolution Fréquentation', 
          value: `${parseFloat(analysis.kpis.freqGrowthToDate) >= 0 ? '+' : ''}${analysis.kpis.freqGrowthToDate}%`,
          positive: parseFloat(analysis.kpis.freqGrowthToDate) >= 0
        },
        { 
          label: 'Évolution PM', 
          value: `${parseFloat(analysis.kpis.pmGrowthToDate) >= 0 ? '+' : ''}${analysis.kpis.pmGrowthToDate}%`,
          positive: parseFloat(analysis.kpis.pmGrowthToDate) >= 0
        }
      ];
      
      evolutions.forEach((evo, index) => {
        const col = index % 3;
        const x = margin + (col * (maxWidth / 3));
        const y = yPos;
        
        // Fond évolution avec couleur selon positive/négative
        if (evo.positive) {
          pdf.setFillColor(236, 253, 245);
        } else {
          pdf.setFillColor(254, 242, 242);
        }
        pdf.rect(x, y - 2, (maxWidth / 3) - 5, 10, 'F');
        
        pdf.setTextColor(26, 26, 26);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(evo.label + ':', x + 2, y + 3);
        
        // Couleur selon évolution
        if (evo.positive) {
          pdf.setTextColor(16, 185, 129);
        } else {
          pdf.setTextColor(239, 68, 68);
        }
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(evo.value, x + 2, y + 7);
        
        pdf.setTextColor(26, 26, 26);
      });
      yPos += 25;
      
      // Synthèse
      pdf.setFillColor(217, 119, 6);
      pdf.rect(margin, yPos, maxWidth, 8, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SYNTHÈSE AUTOMATIQUE', margin + 5, yPos + 6);
      yPos += 20;
      
      // Contenu synthèse avec formatage amélioré
      pdf.setTextColor(26, 26, 26);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Nettoyer et formater la synthèse
      const cleanSummary = analysis.summary
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/<br>/g, '\n')
        .replace(/🟢|🔴|⚠️|🎯|📊|🚀|📈|💡/g, '')
        .trim();
      
      // Diviser la synthèse en sections avec icônes
      const synthesisSections = [
        {
          title: `ANNÉE ${analysis.kpis.yearPrevious} (N-1)`,
          content: [
            `Chiffre d'affaires annuel : ${formatNumber(analysis.kpis.totalRevenueAnnualPrevious)} €`,
            `CA à date : ${formatNumber(analysis.kpis.totalRevenueToDatePrevious)} €`,
            `Fréquentation : ${formatNumber(analysis.kpis.totalFreqToDatePrevious)} visiteurs`,
            `Panier moyen : ${formatNumber(analysis.kpis.avgPMToDatePrevious)} €`
          ]
        },
        {
          title: `ANNÉE ${analysis.kpis.yearCurrent} (N)`,
          content: [
            `Chiffre d'affaires annuel : ${formatNumber(analysis.kpis.totalRevenueAnnualCurrent)} €`,
            `CA à date : ${formatNumber(analysis.kpis.totalRevenueToDateCurrent)} €`,
            `Fréquentation : ${formatNumber(analysis.kpis.totalFreqToDateCurrent)} visiteurs`,
            `Panier moyen : ${formatNumber(analysis.kpis.avgPMToDateCurrent)} €`
          ]
        },
        {
          title: `OBJECTIFS ${analysis.kpis.yearCurrent}`,
          content: [
            `Objectif CA : ${formatNumber(analysis.kpis.totalCAObjectivesAnnual || '0')} €`,
            `Objectif Fréquentation : ${formatNumber(analysis.kpis.totalFreqObjectivesAnnual || '0')} visiteurs`,
            `Objectif PM : ${formatNumber(analysis.kpis.totalPMObjectivesAnnual || '0')} €`,
            `Avancement vs objectif : ${analysis.kpis.objectiveRealizationToDate}%`
          ]
        },
        {
          title: `ÉVOLUTIONS ${analysis.kpis.yearCurrent} vs ${analysis.kpis.yearPrevious} (à date - ${analysis.kpis.currentMonthName})`,
          content: [
            `CA à date : ${parseFloat(analysis.kpis.revenueGrowthToDate) >= 0 ? '+' : ''}${analysis.kpis.revenueGrowthToDate}%`,
            `Fréquentation à date : ${parseFloat(analysis.kpis.freqGrowthToDate) >= 0 ? '+' : ''}${analysis.kpis.freqGrowthToDate}%`,
            `Panier moyen à date : ${parseFloat(analysis.kpis.pmGrowthToDate) >= 0 ? '+' : ''}${analysis.kpis.pmGrowthToDate}%`
          ]
        }
      ];
      
      synthesisSections.forEach((section, sectionIndex) => {
        // Vérifier si nouvelle page nécessaire
        if (yPos > 220) {
          addFooter();
          pdf.addPage();
          currentPage++;
          addHeader(currentPage, totalPages);
          yPos = 40;
        }
        
        // Titre de section
        pdf.setTextColor(217, 119, 6);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(section.title, margin, yPos);
        yPos += 8;
        
        // Contenu de la section
        section.content.forEach((item, itemIndex) => {
          if (yPos > 250) {
            addFooter();
            pdf.addPage();
            currentPage++;
            addHeader(currentPage, totalPages);
            yPos = 40;
          }
          
          pdf.setTextColor(26, 26, 26);
          pdf.setFontSize(10);
          
          // Séparer le label et la valeur
          const [label, ...valueParts] = item.split(' : ');
          const value = valueParts.join(' : ');
          
          // Label en normal
          pdf.setFont('helvetica', 'normal');
          pdf.text(`• ${label} :`, margin + 5, yPos);
          
          // Valeur en gras
          if (value) {
            pdf.setFont('helvetica', 'bold');
            const labelWidth = pdf.getTextWidth(`• ${label} : `);
            
            // Couleur spéciale pour les évolutions
            if (section.title.includes('ÉVOLUTIONS') && (value.includes('%'))) {
              const isPositive = value.includes('+') || (!value.includes('-') && !value.includes('0%'));
              if (isPositive) {
                pdf.setTextColor(16, 185, 129); // Vert
              } else {
                pdf.setTextColor(239, 68, 68); // Rouge
              }
            }
            
            pdf.text(value, margin + 5 + labelWidth, yPos);
            pdf.setTextColor(26, 26, 26); // Reset couleur
          }
          
          yPos += 6;
        });
        
        yPos += 5; // Espacement entre sections
      });
      
      // Ajouter une conclusion si l'espace le permet
      if (yPos < 230) {
        yPos += 10;
        pdf.setTextColor(217, 119, 6);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('POINTS CLÉS', margin, yPos);
        yPos += 8;
        
        pdf.setTextColor(26, 26, 26);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const keyPoints = [
          `Période analysée : ${analysis.kpis.currentMonthName} ${analysis.kpis.yearCurrent}`,
          `Performance vs objectif : ${parseFloat(analysis.kpis.objectiveRealizationToDate) >= 80 ? 'Excellente' : parseFloat(analysis.kpis.objectiveRealizationToDate) >= 60 ? 'Bonne' : 'À améliorer'}`,
          `Tendance générale : ${parseFloat(analysis.kpis.revenueGrowthToDate) >= 0 ? 'Croissance' : 'Décroissance'}`
        ];
        
        keyPoints.forEach(point => {
          if (yPos < 250) {
            pdf.text(`• ${point}`, margin + 5, yPos);
            yPos += 6;
          }
        });
      }
      
      addFooter();
      
      // PAGE 3 - Tableaux de données par métrique
      if (prepareWeeklyComparisonData.data.length > 0) {
        const metrics = [
          { key: 'CA', label: 'Chiffre d\'affaires', unit: '€' },
          { key: 'Fréquentation', label: 'Fréquentation', unit: 'visiteurs' },
          { key: 'PM', label: 'Panier Moyen', unit: '€' }
        ];
        
        metrics.forEach((metric, metricIndex) => {
          // Nouvelle page pour chaque métrique ou si pas assez d'espace
          if (metricIndex === 0 || yPos > 150) {
            if (metricIndex > 0) addFooter();
            pdf.addPage();
            currentPage++;
            addHeader(currentPage, totalPages);
            yPos = 40;
          }
          
          // Préparer les données pour cette métrique spécifique
          const prepareMetricData = (metricKey: string) => {
            // Mapper la clé de métrique
            let dataMetricKey = metricKey;
            if (metricKey === 'Fréquentation') dataMetricKey = 'FREQ';
            
            // Créer une map des objectifs par mois pour la métrique sélectionnée
            const objectivesMap = new Map();
            analysis.objectives.filter(obj => obj.metric === dataMetricKey && obj.year === analysis.kpis.yearCurrent)
              .forEach(obj => {
                objectivesMap.set(obj.type, obj.objective);
              });

            // Créer une map des données mensuelles par mois et année
            const weeklyDataMap = new Map();
            analysis.weeklyData.filter(item => item.metric === dataMetricKey)
              .forEach(item => {
                const key = `S${item.weekNumber}`;
                if (!weeklyDataMap.has(key)) {
                  weeklyDataMap.set(key, {
                    category: `Semaine ${item.weekNumber}`,
                    weekNumber: item.weekNumber,
                    'Année Current': 0,
                    'Année Previous': 0,
                    'Objectif': 0, // Pas d'objectifs hebdomadaires pour le moment
                    variation: 0,
                    'Variation vs Objectif': 0
                  });
                }
                
                const weekData = weeklyDataMap.get(key);
                if (item.year === analysis.kpis.yearCurrent) {
                  weekData['Année Current'] = item.value;
                } else if (item.year === analysis.kpis.yearPrevious) {
                  weekData['Année Previous'] = item.value;
                }
              });

            // Calculer les variations
            const comparisonData = Array.from(weeklyDataMap.values())
              .map(item => {
                const currentValue = item['Année Current'];
                const previousValue = item['Année Previous'];
                const variation = previousValue > 0 ? 
                  ((currentValue - previousValue) / previousValue * 100) : 0;
                
                return {
                  ...item,
                  variation: parseFloat(variation.toFixed(2))
                };
              })
              .sort((a, b) => a.weekNumber - b.weekNumber);

            return comparisonData;
          };
          
          const metricData = prepareMetricData(metric.key);
          
          // Titre tableau
          pdf.setFillColor(26, 26, 26);
          pdf.rect(margin, yPos, maxWidth, 8, 'F');
          
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`TABLEAU ${metric.label.toUpperCase()}`, margin + 5, yPos + 6);
          yPos += 15;
          
          // En-têtes tableau
          const colWidth = maxWidth / 6;
          const headers = ['Semaine', `${analysis.kpis.yearCurrent}`, 'Objectif', `${analysis.kpis.yearPrevious}`, 'Évolution', 'Statut'];
          
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yPos, maxWidth, 8, 'F');
          
          pdf.setTextColor(26, 26, 26);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          
          headers.forEach((header, index) => {
            pdf.text(header, margin + (index * colWidth) + 2, yPos + 5);
          });
          yPos += 10;
          
          // Données tableau (premiers 8 mois pour tenir sur la page)
          metricData.slice(0, 8).forEach((row, index) => {
            const isEven = index % 2 === 0;
            
            if (isEven) {
              pdf.setFillColor(248, 250, 252);
              pdf.rect(margin, yPos - 1, maxWidth, 8, 'F');
            }
            
            pdf.setTextColor(26, 26, 26);
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            
            const formatValue = (value: number) => {
              if (selectedMetric === 'CA') {
                return `${value.toLocaleString('fr-FR')} €`;
              } else if (selectedMetric === 'Fréquentation') {
                return `${Math.round(value).toLocaleString('fr-FR')} visiteurs`;
              } else {
                return `${value.toFixed(2)} €`;
              }
            };
            
            const values = [
              row.category,
              formatValue(row['Année Current']),
              row['Objectif'] > 0 ? formatValue(row['Objectif']) : '-',
              formatValue(row['Année Previous']),
              `${row.variation >= 0 ? '+' : ''}${row.variation.toFixed(1)}%`,
              row.variation >= 0 ? 'Hausse' : 'Baisse'
            ];
            
            values.forEach((value, colIndex) => {
              if (colIndex === 4) { // Évolution
                if (row.variation >= 0) {
                  pdf.setTextColor(16, 185, 129);
                } else {
                  pdf.setTextColor(239, 68, 68);
                }
                pdf.setFont('helvetica', 'bold');
              } else if (colIndex === 5) { // Statut
                if (row.variation >= 0) {
                  pdf.setTextColor(16, 185, 129);
                } else {
                  pdf.setTextColor(239, 68, 68);
                }
                pdf.setFont('helvetica', 'normal');
              } else {
                pdf.setTextColor(26, 26, 26);
                pdf.setFont('helvetica', 'normal');
              }
              
              pdf.text(value, margin + (colIndex * colWidth) + 2, yPos + 4);
            });
            
            yPos += 8;
          });
          
          yPos += 15; // Espacement entre tableaux
        });
        
        addFooter();
      }
      
      // Sauvegarder avec nom personnalisé
      const filename = `Analyse-Financiere-SOCOA-${analysis.kpis.yearCurrent}-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      
      toast.success('Rapport PDF moderne exporté avec succès !');
      
    } catch (error) {
      console.error('Erreur export PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  // Obtenir les métriques disponibles
  const availableMetrics = ['CA', 'Fréquentation', 'PM'];
  const availableYears = [analysis.kpis.yearPrevious.toString(), analysis.kpis.yearCurrent.toString(), 'both'];

  return (
    <div className="space-y-8">
      {/* En-tête avec actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Analyse Financière SOCOA</h1>
          <p className="text-gray-600 mt-1">
            Fichier: <span className="font-medium">{data.filename}</span> • 
            Données jusqu'à {analysis.kpis.currentMonthName} • 
            {data.rawData.rowCount} lignes analysées
            {/* Badge restauration */}
            {isDataRestored && (
              <span className="inline-flex items-center px-2 py-1 ml-2 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 shadow-sm">
                🔄 Données restaurées
              </span>
            )}
          </p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={exportToPDF}
            className="btn-outline space-x-2"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Export PDF</span>
          </button>
          
          <button
            onClick={onGenerateReport}
            className="btn-primary space-x-2"
          >
            <DocumentTextIcon className="h-5 w-5" />
            <span>Générer Rapport</span>
          </button>
          
          <button
            onClick={onNewAnalysis}
            className="btn-secondary space-x-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Nouvelle Analyse</span>
          </button>
        </div>
      </div>

      {/* Cards d'avancement CA */}
      <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
        {/* Carte d'avancement CA principale - À date */}
        <motion.div 
          className="kpi-card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 relative group min-w-[200px]"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className={`text-2xl font-bold mb-1 ${parseFloat(analysis.kpis.objectiveRealizationToDate) >= 80 ? 'text-green-600' : parseFloat(analysis.kpis.objectiveRealizationToDate) >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
            {analysis.kpis.objectiveRealizationToDate}%
          </div>
          <div className="text-sm font-medium text-gray-700">🎯 Avancement CA vs Objectif ({analysis.kpis.currentMonthName})</div>
          
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
            Pourcentage d'avancement du CA à date par rapport à l'objectif {analysis.kpis.yearCurrent} (jusqu'à {analysis.kpis.currentMonthName})
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </motion.div>
        
        {/* Carte d'avancement CA annuel (à titre indicatif) */}
        <motion.div 
          className="kpi-card bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200 relative group min-w-[200px]"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25 }}
        >
          <div className={`text-xl font-bold mb-1 ${parseFloat(analysis.kpis.objectiveRealizationAnnual) >= 80 ? 'text-green-600' : parseFloat(analysis.kpis.objectiveRealizationAnnual) >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
            {analysis.kpis.objectiveRealizationAnnual}%
          </div>
          <div className="text-xs font-medium text-gray-600">📊 Avancement CA Annuel (indicatif)</div>
          
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
            CA annuel complet (12 mois) vs objectif annuel complet - Perspective conservative
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </motion.div>
      </div>

      {/* KPIs - Année N-1 */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h2 className="text-xl font-semibold text-blue-800 mb-4">Année {analysis.kpis.yearPrevious} (N-1)</h2>
        
        {/* Première ligne : Totaux annuels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.totalRevenueAnnualPrevious).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">CA Total Annuel</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.totalFreqAnnualPrevious).toLocaleString('fr-FR')}</div>
            <div className="kpi-label">Fréquentation Total Annuel</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.avgPMAnnualPrevious).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">PM Total Annuel</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.avgMonthlyToDatePrevious).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">CA moyen mensuel</div>
          </motion.div>
        </div>
        
        {/* Deuxième ligne : Données à date */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.totalRevenueToDatePrevious).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">CA ({analysis.kpis.currentMonthName})</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.totalFreqToDatePrevious).toLocaleString('fr-FR')}</div>
            <div className="kpi-label">Fréquentation ({analysis.kpis.currentMonthName})</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <div className="kpi-value text-blue-600">{parseFloat(analysis.kpis.avgPMToDatePrevious).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">Panier Moyen ({analysis.kpis.currentMonthName})</div>
          </motion.div>
        </div>
      </div>

      {/* KPIs - Année N */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <h2 className="text-xl font-semibold text-green-800 mb-4">Année {analysis.kpis.yearCurrent} (N)</h2>
        
        {/* Première ligne : Totaux annuels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.totalRevenueAnnualCurrent).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">CA Total Annuel</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.totalFreqAnnualCurrent).toLocaleString('fr-FR')}</div>
            <div className="kpi-label">Fréquentation Total Annuel</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.avgPMAnnualCurrent).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">PM Total Annuel</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.avgMonthlyToDateCurrent).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">CA moyen mensuel</div>
          </motion.div>
        </div>
        
        {/* Deuxième ligne : Données à date */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.totalRevenueToDateCurrent).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">CA ({analysis.kpis.currentMonthName})</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.totalFreqToDateCurrent).toLocaleString('fr-FR')}</div>
            <div className="kpi-label">Fréquentation ({analysis.kpis.currentMonthName})</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <div className="kpi-value text-green-600">{parseFloat(analysis.kpis.avgPMToDateCurrent).toLocaleString('fr-FR')} €</div>
            <div className="kpi-label">Panier Moyen ({analysis.kpis.currentMonthName})</div>
          </motion.div>
        </div>
        
        {/* Troisième ligne : Objectifs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div 
            className="kpi-card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
          >
            <div className="kpi-value text-amber-600">
              {parseFloat(analysis.kpis.totalCAObjectivesAnnual || '0').toLocaleString('fr-FR')} €
            </div>
            <div className="kpi-label">🎯 Objectif CA</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <div className="kpi-value text-amber-600">
              {parseFloat(analysis.kpis.totalFreqObjectivesAnnual || '0').toLocaleString('fr-FR')}
            </div>
            <div className="kpi-label">🎯 Objectif Fréquentation</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
          >
            <div className="kpi-value text-amber-600">
              {parseFloat(analysis.kpis.totalPMObjectivesAnnual || '0').toLocaleString('fr-FR')} €
            </div>
            <div className="kpi-label">🎯 Objectif PM</div>
          </motion.div>
        </div>
      </div>

      {/* Évolutions */}
      <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
        <h2 className="text-xl font-semibold text-purple-800 mb-4">Évolutions {analysis.kpis.yearCurrent} vs {analysis.kpis.yearPrevious} ({analysis.kpis.currentMonthName})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div className={`kpi-value ${parseFloat(analysis.kpis.revenueGrowthToDate) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {parseFloat(analysis.kpis.revenueGrowthToDate) >= 0 ? '+' : ''}{analysis.kpis.revenueGrowthToDate}%
            </div>
            <div className="kpi-label">Évolution CA</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            <div className={`kpi-value ${parseFloat(analysis.kpis.freqGrowthToDate) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {parseFloat(analysis.kpis.freqGrowthToDate) >= 0 ? '+' : ''}{analysis.kpis.freqGrowthToDate}%
            </div>
            <div className="kpi-label">Évolution Fréquentation</div>
          </motion.div>
          
          <motion.div 
            className="kpi-card bg-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <div className={`kpi-value ${parseFloat(analysis.kpis.pmGrowthToDate) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {parseFloat(analysis.kpis.pmGrowthToDate) >= 0 ? '+' : ''}{analysis.kpis.pmGrowthToDate}%
            </div>
            <div className="kpi-label">Évolution PM</div>
          </motion.div>
        </div>
      </div>

      {/* Navigation des graphiques avec filtres */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between bg-white rounded-lg shadow-md p-4 space-y-4 lg:space-y-0">
        <div className="flex flex-wrap space-x-2">
          <button
            onClick={() => setActiveChart('monthly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeChart === 'monthly' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Données Mensuelles
          </button>
          <button
            onClick={() => setActiveChart('weekly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeChart === 'weekly' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Données Hebdomadaires
          </button>
        </div>
        
        <div className="flex flex-wrap items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="CA">Chiffre d'affaires</option>
              <option value="Fréquentation">Fréquentation</option>
              <option value="PM">Panier Moyen</option>
            </select>
          </div>
          
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="both">Comparaison N vs N-1</option>
            <option value={analysis.kpis.yearPrevious.toString()}>Année {analysis.kpis.yearPrevious} (N-1)</option>
            <option value={analysis.kpis.yearCurrent.toString()}>Année {analysis.kpis.yearCurrent} (N)</option>
          </select>
        </div>
      </div>

      {/* Graphiques */}
      <motion.div 
        className="analysis-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="analysis-title">
          <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
          Visualisation des Données - {selectedMetric}
        </div>

        {activeChart === 'monthly' && (
          <div className="space-y-6">
            {/* Graphique */}
            <div className="chart-container" style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={prepareMonthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value: number, name: string) => {
                      if (name === 'Objectif') {
                        return [
                          selectedMetric === 'CA' ? `${value.toLocaleString('fr-FR')} €` :
                          selectedMetric === 'Fréquentation' ? `${Math.round(value).toLocaleString('fr-FR')} visiteurs` :
                          `${value.toLocaleString('fr-FR')} €`,
                          'Objectif'
                        ];
                      }
                      return [
                        selectedMetric === 'CA' ? `${value.toLocaleString('fr-FR')} €` :
                        selectedMetric === 'Fréquentation' ? `${Math.round(value).toLocaleString('fr-FR')} visiteurs` :
                        `${value.toLocaleString('fr-FR')} €`,
                        name
                      ];
                    }}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Legend />
                  {selectedYear === 'both' ? (
                    <>
                      <Bar dataKey={analysis.kpis.yearPrevious.toString()} fill="#6B7280" name={`${analysis.kpis.yearPrevious} (N-1)`} />
                      <Bar dataKey={analysis.kpis.yearCurrent.toString()} fill="#3B82F6" name={`${analysis.kpis.yearCurrent} (N)`} />
                    </>
                  ) : (
                    <Bar 
                      dataKey={selectedYear} 
                      fill={selectedYear === analysis.kpis.yearPrevious.toString() ? '#6B7280' : '#3B82F6'} 
                      name={`${selectedMetric} ${selectedYear}`} 
                    />
                  )}
                  {/* Ligne d'objectif */}
                  <Line 
                    type="monotone" 
                    dataKey="objectif" 
                    stroke="#F59E0B" 
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ fill: '#F59E0B', r: 4 }}
                    name="Objectif"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            
            {/* Tableau de comparaison */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <TableCellsIcon className="h-5 w-5 text-blue-600 mr-2" />
                Tableau de Comparaison Mensuelle - {selectedMetric}
              </h3>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th>{analysis.kpis.yearCurrent} (N)</th>
                      <th>Objectif {analysis.kpis.yearCurrent}</th>
                      <th>Variation vs Objectif</th>
                      <th>{analysis.kpis.yearPrevious} (N-1)</th>
                      <th>Variation N vs N-1</th>
                      <th>Évolution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prepareComparisonData.map((comp, index) => {
                      const variationVsObjectif = comp['Variation vs Objectif'];
                      const isObjectiveFarMissed = variationVsObjectif < -10; // Plus de 10% en dessous de l'objectif
                      
                      // Vérifier si le mois a des données (valeur actuelle > 0)
                      const hasCurrentData = comp['Année Current'] > 0;
                      const hasPreviousData = comp['Année Previous'] > 0;
                      
                      // Fonction pour formater les valeurs selon la métrique
                      const formatValue = (value: number) => {
                        if (selectedMetric === 'CA') {
                          return `${value.toLocaleString('fr-FR')} €`;
                        } else if (selectedMetric === 'Fréquentation') {
                          return `${Math.round(value).toLocaleString('fr-FR')} visiteurs`;
                        } else {
                          return `${value.toFixed(2)} €`;
                        }
                      };
                      
                      return (
                        <tr key={index}>
                          <td className="font-medium">{comp.category}</td>
                          <td>{hasCurrentData ? formatValue(comp['Année Current']) : '-'}</td>
                          <td className="text-amber-600 font-medium">
                            {comp['Objectif'] > 0 ? formatValue(comp['Objectif']) : '-'}
                          </td>
                          <td className={`font-medium ${
                            !hasCurrentData ? 'text-gray-500' :
                            comp['Objectif'] === 0 ? 'text-gray-500' :
                            variationVsObjectif > 0 ? '!text-green-600' :
                            variationVsObjectif < 0 ? '!text-red-600' :
                            'text-gray-600'
                          }`}>
                            {!hasCurrentData ? '-' :
                             comp['Objectif'] > 0 ? `${variationVsObjectif > 0 ? '+' : ''}${variationVsObjectif.toFixed(1)}%` : '-'}
                          </td>
                          <td>{hasPreviousData ? formatValue(comp['Année Previous']) : '-'}</td>
                          <td className={`font-medium ${
                            !hasCurrentData ? 'text-gray-500' :
                            comp.variation >= 0 ? '!text-green-600' : '!text-red-600'
                          }`}>
                            {!hasCurrentData ? '-' :
                             `${comp.variation >= 0 ? '+' : ''}${comp.variation.toFixed(2)}%`}
                          </td>
                          <td>
                            {hasCurrentData ? (
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  comp.variation >= 0 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {comp.variation >= 0 ? '↗ Hausse' : '↘ Baisse'}
                                </span>
                                {isObjectiveFarMissed && comp['Objectif'] > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    🚨 Objectif loin
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-sm">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeChart === 'weekly' && (
          <div className="space-y-6">
            {!analysis.weeklyData || analysis.weeklyData.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">
                  <ChartBarIcon className="h-16 w-16 mx-auto mb-2 text-gray-300" />
                  Aucune donnée hebdomadaire disponible
                </div>
                <p className="text-sm text-gray-600">
                  Assurez-vous d'avoir une feuille "suivi hebdo" dans votre fichier Excel.
                </p>
              </div>
            ) : (
              <>
                {/* Graphique à barres comparatif */}
                <div className="chart-container" style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={prepareWeeklyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="week" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value: number, name: string) => {
                          if (name === 'Objectif') {
                            return [
                              selectedMetric === 'CA' ? `${value.toLocaleString('fr-FR')} €` :
                              selectedMetric === 'Fréquentation' ? `${Math.round(value).toLocaleString('fr-FR')} visiteurs` :
                              `${value.toLocaleString('fr-FR')} €`,
                              'Objectif'
                            ];
                          }
                          return [
                            selectedMetric === 'CA' ? `${value.toLocaleString('fr-FR')} €` :
                            selectedMetric === 'Fréquentation' ? `${Math.round(value).toLocaleString('fr-FR')} visiteurs` :
                            `${value.toLocaleString('fr-FR')} €`,
                            name
                          ];
                        }}
                        labelStyle={{ color: '#374151' }}
                      />
                      <Legend />
                      {selectedYear === 'both' ? (
                        <>
                          <Bar dataKey={analysis.kpis.yearPrevious.toString()} fill="#6B7280" name={`${analysis.kpis.yearPrevious} (N-1)`} />
                          <Bar dataKey={analysis.kpis.yearCurrent.toString()} fill="#3B82F6" name={`${analysis.kpis.yearCurrent} (N)`} />
                        </>
                      ) : (
                        <Bar 
                          dataKey={selectedYear} 
                          fill={selectedYear === analysis.kpis.yearPrevious.toString() ? '#6B7280' : '#3B82F6'} 
                          name={`${selectedMetric} ${selectedYear}`} 
                        />
                      )}
                      {/* Ligne d'objectif hebdomadaire */}
                      <Line 
                        type="monotone" 
                        dataKey="objectif" 
                        stroke="#F59E0B" 
                        strokeWidth={3}
                        strokeDasharray="5 5"
                        dot={{ fill: '#F59E0B', r: 4 }}
                        name="Objectif"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Tableau de comparaison hebdomadaire avec pagination */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <TableCellsIcon className="h-5 w-5 text-blue-600 mr-2" />
                      Tableau de Comparaison Hebdomadaire - {selectedMetric}
                    </h3>
                    <div className="text-sm text-gray-600">
                      {prepareWeeklyComparisonData.totalItems} semaines • Page {weeklyPage} sur {prepareWeeklyComparisonData.totalPages}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Semaine</th>
                          <th>{analysis.kpis.yearCurrent} (N)</th>
                          <th>Objectif {analysis.kpis.yearCurrent}</th>
                          <th>Variation vs Objectif</th>
                          <th>{analysis.kpis.yearPrevious} (N-1)</th>
                          <th>Variation N vs N-1</th>
                          <th>Évolution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prepareWeeklyComparisonData.data.map((comp: any, index: number) => {
                          // Vérifier si la semaine a des données
                          const hasCurrentData = comp['Année Current'] > 0;
                          const hasPreviousData = comp['Année Previous'] > 0;
                          const variationVsObjectif = comp['Variation vs Objectif'];
                          const isObjectiveFarMissed = variationVsObjectif < -10; // Plus de 10% en dessous de l'objectif
                          
                          // Fonction pour formater les valeurs selon la métrique
                          const formatValue = (value: number) => {
                            if (selectedMetric === 'CA') {
                              return `${value.toLocaleString('fr-FR')} €`;
                            } else if (selectedMetric === 'Fréquentation') {
                              return `${Math.round(value).toLocaleString('fr-FR')} visiteurs`;
                            } else {
                              return `${value.toFixed(2)} €`;
                            }
                          };
                          
                          return (
                            <tr key={index}>
                              <td className="font-medium">{comp.category}</td>
                              <td>{hasCurrentData ? formatValue(comp['Année Current']) : '-'}</td>
                              <td className="text-amber-600 font-medium">
                                {comp['Objectif'] > 0 ? formatValue(comp['Objectif']) : '-'}
                              </td>
                              <td className={`font-medium ${
                                !hasCurrentData ? 'text-gray-500' :
                                comp['Objectif'] === 0 ? 'text-gray-500' :
                                variationVsObjectif > 0 ? '!text-green-600' :
                                variationVsObjectif < 0 ? '!text-red-600' :
                                'text-gray-600'
                              }`}>
                                {!hasCurrentData ? '-' :
                                 comp['Objectif'] > 0 ? `${variationVsObjectif > 0 ? '+' : ''}${variationVsObjectif.toFixed(1)}%` : '-'}
                              </td>
                              <td>{hasPreviousData ? formatValue(comp['Année Previous']) : '-'}</td>
                              <td className={`font-medium ${
                                !hasCurrentData || !hasPreviousData ? 'text-gray-500' :
                                comp.variation >= 0 ? '!text-green-600' : '!text-red-600'
                              }`}>
                                {hasCurrentData && hasPreviousData ? 
                                 `${comp.variation >= 0 ? '+' : ''}${comp.variation.toFixed(2)}%` : '-'}
                              </td>
                              <td>
                                {hasCurrentData ? (
                                  <div className="flex items-center space-x-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      comp.variation >= 0 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {comp.variation >= 0 ? '↗ Hausse' : '↘ Baisse'}
                                    </span>
                                    {isObjectiveFarMissed && comp['Objectif'] > 0 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        🚨 Objectif loin
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-500 text-sm">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {prepareWeeklyComparisonData.totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2 mt-4">
                      <button
                        onClick={() => setWeeklyPage(prev => Math.max(1, prev - 1))}
                        disabled={weeklyPage === 1}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      
                      <div className="flex space-x-1">
                        {Array.from({ length: prepareWeeklyComparisonData.totalPages }, (_, i) => i + 1)
                          .filter(page => 
                            page === 1 || 
                            page === prepareWeeklyComparisonData.totalPages || 
                            Math.abs(page - weeklyPage) <= 1
                          )
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && (
                                <span className="px-2 py-1 text-sm text-gray-500">...</span>
                              )}
                              <button
                                onClick={() => setWeeklyPage(page)}
                                className={`px-3 py-1 text-sm rounded-md ${
                                  weeklyPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))
                        }
                      </div>
                      
                      <button
                        onClick={() => setWeeklyPage(prev => Math.min(prepareWeeklyComparisonData.totalPages, prev + 1))}
                        disabled={weeklyPage === prepareWeeklyComparisonData.totalPages}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </motion.div>

      {/* Graphiques avancés et analyses BI */}
      <motion.div 
        className="analysis-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <div className="analysis-title">
          <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
          Analyses Avancées & Business Intelligence
        </div>
        <AdvancedCharts data={data} />
      </motion.div>
    </div>
  );
};

export default AnalysisResults;